/**
 * QR bill panel for an operator-initiated deposit — the sibling of `BankAccountInfo` for a
 * deposit opened as a T-PayGate bill.
 *
 * The admin counterpart of the customer console's `TPayGateQrPanel`, and the same rules: the
 * gateway's own image when it sent one, else the VietQR payload rendered through TPOS; a white
 * plate under the code so it scans in dark mode; the code taken away once the bill has expired.
 * The operator is usually not the one who pays, so the code can be downloaded to send on.
 */

import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Download,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currency";
import { formatDateTime } from "@/lib/utils";
import type { TenantDepositQrBill } from "@/types/tenant-deposit";

interface QrBillInfoProps {
  amount: number;
  currency: string;
  /** The payment's own number (WIC-PAY-…). Shown, and names the downloaded image. */
  paymentOrderNumber: string;
  bill: TenantDepositQrBill;
}

export function QrBillInfo({
  amount,
  currency,
  paymentOrderNumber,
  bill,
}: QrBillInfoProps) {
  const hasExpired = useHasExpired(bill.expiredAt);

  // The gateway's image first; the raw payload rendered through TPOS when it sent none.
  const qrSrc = resolveQrSrc(bill.qrImageBase64, bill.qrContent);
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  // Null while there is no code to show: none was sent, it failed to load, or the bill expired.
  const codeSrc = hasExpired || qrSrc === brokenSrc ? null : qrSrc;

  const [copied, setCopied] = useState(false);
  const handleCopyBillCode = async () => {
    // The Clipboard API is absent on an insecure origin (plain http in dev).
    if (!navigator.clipboard) {
      toast.error("Copying is not available on this connection");
      return;
    }
    try {
      await navigator.clipboard.writeText(bill.billCode);
    } catch {
      toast.error("Copying is not available on this connection");
      return;
    }
    setCopied(true);
    toast.success("Transfer content copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3">
      {/* Read before the scan: T-PayGate matches the money by the transfer content and settles
          only the exact amount, so an edited transfer is never confirmed on its own. */}
      {codeSrc && (
        <div
          role="note"
          className="flex w-full items-start gap-2 rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-left text-xs"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
          <p>
            The amount and the transfer content must not be changed. A transfer
            with a different amount or content is not confirmed automatically.
          </p>
        </div>
      )}

      <p className="text-3xl font-bold tabular-nums text-primary">
        {formatCurrency(amount, currency)}
      </p>

      {hasExpired ? (
        // Taken away rather than greyed out: a scan after the deadline is refused by the bank.
        <div
          role="alert"
          className="flex min-h-[240px] w-full max-w-[240px] items-center justify-center rounded-lg border border-warn/40 bg-warn-soft p-4 text-center text-sm"
        >
          This QR code has expired. Cancel the payment and open a new deposit to
          get a new code.
        </div>
      ) : (
        codeSrc && (
          <img
            src={codeSrc}
            alt="VietQR payment code"
            // A white plate in both themes: a QR only scans as dark-on-light.
            className="w-full max-w-[240px] rounded-lg border bg-white p-2 dark:bg-white"
            onError={() => setBrokenSrc(codeSrc)}
          />
        )
      )}

      {bill.expiredAt && !hasExpired && (
        <p className="text-xs text-muted-foreground">
          Expires at {formatDateTime(bill.expiredAt)}
        </p>
      )}

      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Payment code</dt>
        <dd className="break-all font-mono">{paymentOrderNumber}</dd>
        <dt className="text-muted-foreground">Transfer content</dt>
        <dd className="flex items-center gap-1 break-all font-mono">
          {bill.billCode}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            aria-label="Copy the transfer content"
            onClick={() => void handleCopyBillCode()}
          >
            {copied ? (
              <CheckCircle className="h-3.5 w-3.5 text-ok" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </dd>
      </dl>

      {codeSrc && (
        <DownloadQrButton
          src={codeSrc}
          fileName={`QR-${paymentOrderNumber}.png`}
        />
      )}
    </div>
  );
}

/** The longest delay `setTimeout` can hold, about 24.8 days. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** Whether `expiredAt` has passed. Flips on its own at the deadline, while the screen stays open. */
function useHasExpired(expiredAt?: string | null): boolean {
  const expiryMs = expiredAt ? new Date(expiredAt).getTime() : Number.NaN;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (Number.isNaN(expiryMs)) return;

    const remainingMs = expiryMs - Date.now();
    if (remainingMs <= 0 || remainingMs > MAX_TIMEOUT_MS) return;

    const timer = setTimeout(() => setNow(Date.now()), remainingMs);
    return () => clearTimeout(timer);
  }, [expiryMs]);

  return !Number.isNaN(expiryMs) && now >= expiryMs;
}

/** The image to show for a bill, or null when the gateway sent neither an image nor a payload. */
function resolveQrSrc(
  qrImageBase64?: string | null,
  qrContent?: string | null,
): string | null {
  if (qrImageBase64) {
    return qrImageBase64.startsWith("data:")
      ? qrImageBase64
      : `data:image/png;base64,${qrImageBase64}`;
  }
  if (qrContent) {
    return `https://statics.tpos.vn/Web/QRcode?value=${encodeURIComponent(qrContent)}&size=240x240`;
  }
  return null;
}

interface DownloadQrButtonProps {
  /** The image on screen: a `data:` URL from the gateway, or the TPOS-rendered fallback. */
  src: string;
  fileName: string;
}

/** Saves the code as an image, to send to the customer who pays. */
function DownloadQrButton({ src, fileName }: DownloadQrButtonProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // The fallback image is on another origin, where the browser ignores `download` and opens
      // the image instead — so it is fetched into a blob first. A `data:` URL saves as it is.
      let href = src;
      if (!src.startsWith("data:")) {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`QR image request failed: ${response.status}`);
        }
        href = URL.createObjectURL(await response.blob());
        // Released after the click has had time to start the save.
        setTimeout(() => URL.revokeObjectURL(href), 10_000);
      }

      const link = document.createElement("a");
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error(
        "Could not download the QR code. Take a screenshot of it instead.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void handleDownload()}
      disabled={downloading}
    >
      {downloading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Download className="mr-2 h-4 w-4" />
      )}
      Download QR code
    </Button>
  );
}
